/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
 

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import "./Tooltip.css";
import styled from "styled-components";
import {
  RxCaretDown,
  RxFontBold,
  RxFontItalic,
  RxTextAlignCenter,
  RxTextAlignLeft,
  RxTextAlignRight,
  RxTrash,
  RxUnderline,
} from "react-icons/rx";
import { BiFont } from "react-icons/bi";
import { AiOutlineHighlight } from "react-icons/ai";
import { TbMathFunction, TbSubscript, TbSuperscript } from "react-icons/tb";
import { Icon } from "./styles";
import DOM from "./dom";
import { MdTextDecrease, MdTextIncrease } from "react-icons/md";
import { RGBAtoHEX, compareHexColor, toKeys } from "./utils";
import Modal from "./Modal";
import LatexEditor from "./LatextEditor";
import katex from "katex";
let baseNode = null;
let extentNode = null;
const COLORS = [
  "#000000",
  "#7f7f7f",
  "#880015",
  "#ed1c24",
  "#ff7f27",
  "#fff200",
  "#22b14c",
  "#00a2e8",
  "#3f48cc",
  "#a349a4",
  "#ffffff",
  "#c3c3c3",
  "#b97a57",
  "#ffaec9",
  "#ffc90e",
  "#efe4b0",
  "#b5e61d",
  "#99d9ea",
  "#7092be",
  "#c8bfe7",
];

const FONT_SIZE = { 10: 1, 13: 2, 16: 3, 18: 4, 24: 5, 32: 6, 48: 7 };

const handleFontSize = (mode) => {
  const selection = window.pdfDoc.getSelection();
  const selectedEl = selection.focusNode.parentElement;

  let { fontSize } = window.getComputedStyle(selectedEl);
  fontSize = +fontSize.match(/\d+/g)[0];

  const minFontSize = +(toKeys(FONT_SIZE).findLast((d) => d < fontSize) ?? 10);
  const maxFontSize = +(toKeys(FONT_SIZE).find((d) => d > fontSize) ?? 48);

  const cmd = "fontSize";
  switch (mode) {
    case "increase":
      DOM.execCommand(cmd, null, FONT_SIZE[maxFontSize]);
      break;
    case "decrease":
      DOM.execCommand(cmd, null, FONT_SIZE[minFontSize]);
      break;
  }
};

const ColorBox = styled.div`
  width: 26px;
  height: 26px;
  background: ${({ color }) => color};
  border: 1px solid transparent;
  transition: all 0.026s ease-in-out 0s;
  &:hover {
    border: 1px solid;
    background: ${({ color }) => color};
    // transform: scale(1.05);
  }
`;

function ColorSelect(props) {
  const [color, setColor] = useState(props.selectColor);

  const handleClick = (e, idx) => {
    e.preventDefault();
    setColor(COLORS[idx]);
    props.onClick();
  };

  return (
    <div className="tooltipbar--drop-select--main-cont">
      <div className="tooltipbar--drop-select-cont">
        <div
          className="tooltipbar--drop-select"
          onClick={(e) => props.onClick(e, color)}
        >
          {props.icon}
          <div className="color-disp--rect" style={{ background: color }} />
        </div>

        <div className="flex-row" onClick={props.onSelect}>
          <RxCaretDown />
        </div>
      </div>
      {props.showOption && (
        <div className="color-picker--cont" id={`color-box--${props.id}`}>
          {COLORS.map((c, idx) => (
            <ColorBox
              key={idx}
              color={c}
              onClick={(e) => handleClick(e, idx)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Tooltip(props) {
  const { key, pageIndex, index } = props.metadata;
  const [showOption, setShowOption] = useState({ fore: false, back: false });
  const [openModal, setOpenModal] = useState(false);
  const [color, setColor] = useState({
    foreColor: COLORS[0],
    backColor: COLORS[5],
  });
  const latexRef = useRef(null);

  const handleColorSet = (type, color_) => {
    let color__ = color_;
    if (type === "backColor") {
      if (compareHexColor(RGBAtoHEX(DOM.isCommandValue("backColor")), color_)) {
        color__ = "#ffffff";
      }
    }
    DOM.execCommand(type, false, color__);
    setColor({ ...color, [type]: color_ });
  };

  const handleToggle = (type, value) => {
    setShowOption({ fore: false, back: false, [type]: value });
  };

  useEffect(() => {
    if (!props.focused) {
      setShowOption(false);
    }
  }, [props.focused]);

  useEffect(() => {
    const tbox = props?.inputRef?.current;
    if (!tbox) {
      return;
    }
    tbox.addEventListener("keyup", handleActive);
    tbox.addEventListener("mouseup", handleActive);

    return () => {
      tbox.removeEventListener("keyup", handleActive);
      tbox.removeEventListener("mouseup", handleActive);
    };
  }, []);

  function handleActive(e) {
    controls.forEach((c, idx) => {
      DOM.setActive(`tooltip-icon--${key}--${idx}`, c.id, "#eaeaea");
    });
  }

  function handleFn(cmd, value) {
    DOM.execCommand(cmd, false, value);
    handleActive();
  }

  const controls = useMemo(
    () => [
      {
        label: "Font Color",
        icon: (
          <ColorSelect
            id={key}
            selectColor={"#000"}
            focused={props.focused}
            icon={<BiFont />}
            showOption={showOption.fore}
            onClick={(e, color) => {
              color && handleColorSet("foreColor", color);
              handleToggle("fore", false);
            }}
            onSelect={() => handleToggle("fore", !showOption.fore)}
          />
        ),
        handleFn: () => {},
      },
      {
        id: "backColor",
        label: "Highlighter",
        icon: (
          <ColorSelect
            id={key}
            selectColor={COLORS[5]}
            focused={props.focused}
            icon={<AiOutlineHighlight />}
            showOption={showOption.back}
            onClick={(e, color) => {
              color && handleColorSet("backColor", color);
              handleToggle("back", false);
            }}
            onSelect={() => handleToggle("back", !showOption.back)}
          />
        ),
      },
      {
        id: "fontSize",
        label: "Increase Font Size",
        icon: <MdTextIncrease />,
        handleFn: (id) => handleFontSize("increase"),
      },
      {
        id: "fontSize",
        label: "Decrease Font Size",
        icon: <MdTextDecrease />,
        handleFn: (id) => handleFontSize("decrease"),
      },
      {
        id: "bold",
        label: "Bold",
        icon: <RxFontBold />,
        handleFn,
      },
      {
        id: "italic",
        label: "Italic",
        icon: <RxFontItalic />,
        handleFn,
      },
      {
        id: "underline",
        label: "Underline",
        icon: <RxUnderline />,
        handleFn,
      },
      {
        id: "superscript",
        label: "Super Script",
        icon: <TbSuperscript />,
        handleFn,
      },
      {
        id: "subscript",
        label: "Sub Script",
        icon: <TbSubscript />,
        handleFn,
      },
      {
        id: "insertHTML",
        label: "Insert Math Equation",
        icon: <TbMathFunction />,
        handleFn: (id, value) => {
          const promise = new Promise((resolve, reject) => {
            const selection = window?.pdfDoc?.getSelection?.();

            baseNode = {
              node: selection.baseNode,
              offset: selection.baseOffset,
            };
            extentNode = {
              node: selection.extentNode,
              offset: selection.extentOffset,
            };

            resolve({ selection, baseNode, extentNode });
          });
          promise
            .then((...args) => {
              console.log(args);
            })
            .then(() => {
              setOpenModal(!openModal);
            });
        },
      },
      {
        id: "justifyLeft",
        label: "Left Align",
        icon: <RxTextAlignLeft />,
        handleFn,
      },
      {
        id: "justifyCenter",
        label: "Center Align",
        icon: <RxTextAlignCenter />,
        handleFn,
      },
      {
        id: "justifyRight",
        label: "Right Align",
        icon: <RxTextAlignRight />,
        handleFn,
      },
      {
        id: "delete",
        label: "Delete",
        icon: <RxTrash />,
        handleFn: () => props.onDelete(key, index),
      },
    ],
    [showOption, color, props]
  );
  function handleUnMountOverlay(root, insert) {
    root && root.unmount();
    setOpenModal(false);

    if (!insert) {
      return;
    }

    const range = new Range();

    range.setStart(baseNode.node, baseNode.offset);
    range.setEnd(extentNode.node, extentNode.offset);

    window.pdfDoc.getSelection().removeAllRanges();
    window.pdfDoc.getSelection().addRange(range);

    const latexHTML =
      katex.renderToString(latexRef.code, {
        throwOnError: false,
        output: "mathml",
      }) + "&nbsp";

    props.inputRef.current.contentEditable = "true";
    DOM.execCommand("insertHTML", null, latexHTML);
    props.inputRef.current.contentEditable = "false";
  }

  function handleAddLatex(e, root) {
    if (!(baseNode && extentNode)) {
      return;
    }
    handleUnMountOverlay(root, true);
  }

  useEffect(() => {
    if (!openModal) {
      return;
    }

    const root = createRoot(window?.pdfDoc?.getElementById("overlay"));
    window.pdfWin.getSelection().removeAllRanges();
    root.render(
      <div className="latex-modal-backdrop">
        <Modal
          open={true}
          title={"LaTeX Editor"}
          clickAway={false}
          onClose={() => handleUnMountOverlay(root)}
          style={{ width: 450, height: 330 }}
          body={<LatexEditor ref={latexRef} code={null} />}
          footer={
            <div className="latex-footer">
              <Icon
                id={`latex-insert--${key}`}
                style={{
                  padding: "16px 26px",
                  color: "#fff",
                  background: "#0078d4",
                }}
                onClick={(e) => handleAddLatex(e, root)}
              >
                Add
              </Icon>
            </div>
          }
        />
      </div>
    );
  }, [openModal]);

  return (
    <div
      ref={props.toolbarRef}
      id={props.id ?? ""}
      className={`pdft-tbox-tooltip--cont`}
      style={{
        // position: "sticky",
        display: props.focused ? "flex" : "none",
        transform: props.getTextBoxPosition(),
      }}
    >
      {controls.map((d, idx) => {
        return (
          <Icon
            key={`tooltip--icon-key--${idx}`}
            id={`tooltip-icon--${key}--${idx}`}
            className="pdf--icon"
            title={d.label}
            onClick={(e, value) => controls[idx]?.handleFn?.(d.id, value)}
          >
            {d.icon}
          </Icon>
        );
      })}
    </div>
  );
}

export default Tooltip;
